import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaChartBar as FaChartBarRaw,
  FaClock as FaClockRaw,
  FaEdit as FaEditRaw,
  FaFileAlt as FaFileAltRaw,
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';
import { teacherService } from '../../services/classroom/teacherService';
import { parseWorkDescription, questionTypeLabel } from './assignmentWorkContent';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChartBar: any = FaChartBarRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClock: any = FaClockRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEdit: any = FaEditRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileAlt: any = FaFileAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUsers: any = FaUsersRaw;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  color: var(--primary);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1rem 1.1rem;
`;

const HeroHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.85rem;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  font-family: 'Poppins', sans-serif;
`;

const HeroMeta = styled.div`
  margin-top: 0.35rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.84rem;
`;

const EditLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--primary);
  color: var(--onPrimary);
  text-decoration: none;
  border-radius: 10px;
  padding: 0.48rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 700;
  &:hover {
    background: var(--primaryDark);
  }
`;

const GoLiveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--success, #1a8f5c);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.48rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.7rem;
  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  background: var(--surface);
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--textSecondary, var(--onSurfaceVariant));
  letter-spacing: 0.04em;
`;

const StatValue = styled.div`
  margin-top: 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.7rem;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const Muted = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.88rem;
`;

const QuestionList = styled.ul`
  margin: 0;
  padding-left: 1.15rem;
  display: grid;
  gap: 0.45rem;
  color: var(--text);
  font-size: 0.88rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--textSecondary, var(--onSurfaceVariant));
  border-bottom: 1px solid var(--outline);
  padding: 0.55rem 0.5rem;
`;

const Td = styled.td`
  border-bottom: 1px solid var(--outline);
  padding: 0.65rem 0.5rem;
  font-size: 0.84rem;
  color: var(--text);
`;

const Status = styled.span<{ $tone: 'ok' | 'warn' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  ${({ $tone }) =>
    $tone === 'ok'
      ? 'background: color-mix(in srgb, var(--success, #1a8f5c) 16%, transparent); color: var(--success, #1a8f5c);'
      : $tone === 'warn'
        ? 'background: color-mix(in srgb, var(--warning, #b45309) 16%, transparent); color: var(--warning, #b45309);'
        : 'background: var(--surfaceVariant); color: var(--textSecondary, var(--onSurfaceVariant));'}
`;

function isFinalizedSubmissionStatus(status: 'submitted' | 'missing' | 'graded' | 'returned'): boolean {
  return status === 'submitted' || status === 'graded' || status === 'returned';
}

function statusLabel(status: 'submitted' | 'missing' | 'graded' | 'returned') {
  if (status === 'graded') return { label: 'Graded', tone: 'ok' as const };
  if (status === 'returned') return { label: 'Returned', tone: 'ok' as const };
  if (status === 'submitted') return { label: 'Submitted', tone: 'warn' as const };
  return { label: 'Missing', tone: 'muted' as const };
}

function statusPriority(status: 'submitted' | 'missing' | 'graded' | 'returned'): number {
  if (status === 'graded' || status === 'returned') return 3;
  if (status === 'submitted') return 2;
  return 1;
}

function rowTimestamp(value?: string | null): number {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

const ClassroomTeacherAssignmentDetailPage: React.FC = () => {
  const { assignmentId = '' } = useParams<{ assignmentId: string }>();
  const queryClient = useQueryClient();

  const assignmentQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', 'assignment', assignmentId],
    queryFn: () => teacherService.getWorkById('assignment', assignmentId),
    enabled: Boolean(assignmentId),
  });

  const overviewQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'overview'],
    queryFn: teacherService.getOverview,
  });

  const submissionsQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', 'assignment', assignmentId, 'submissions'],
    queryFn: () =>
      teacherService.getSubmissionsForWork('assignment', assignmentId, {
        classId: assignmentQuery.data?.class_id,
        workTitle: assignmentQuery.data?.title,
      }),
    enabled: Boolean(assignmentId) && Boolean(assignmentQuery.data),
  });

  const classStudentsQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'class', assignmentQuery.data?.class_id, 'students'],
    queryFn: () => teacherService.getClassStudents(assignmentQuery.data!.class_id),
    enabled: Boolean(assignmentQuery.data?.class_id),
  });

  const publishMutation = useMutation({
    mutationFn: () => teacherService.publishWork('assignment', assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'assignment', assignmentId] });
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'assignment'] });
    },
  });

  const assignment = assignmentQuery.data;
  const className = useMemo(() => {
    if (!assignment) return 'Class';
    return (overviewQuery.data ?? []).find((row) => row.id === assignment.class_id)?.name ?? 'Class';
  }, [assignment, overviewQuery.data]);

  const parsed = useMemo(() => parseWorkDescription(assignment?.description), [assignment?.description]);
  const submissions = useMemo(() => {
    const rows = submissionsQuery.data ?? [];
    const students = classStudentsQuery.data ?? [];
    if (students.length === 0) return rows;

    const latestByStudentId = new Map<string, (typeof rows)[number]>();
    const latestByStudentName = new Map<string, (typeof rows)[number]>();
    rows.forEach((row) => {
      const studentId = row.student_id?.trim() ?? '';
      const normalizedName = row.student_name.trim().toLowerCase();
      const putLatest = (prev: (typeof rows)[number] | undefined, current: (typeof rows)[number]) => {
        if (!prev) return current;
        const prevIsMissing = prev.status === 'missing';
        const currIsMissing = current.status === 'missing';
        if (prevIsMissing !== currIsMissing) {
          return currIsMissing ? prev : current;
        }
        const prevTs = rowTimestamp(prev.submitted_at);
        const currTs = rowTimestamp(current.submitted_at);
        if (currTs > prevTs) return current;
        if (currTs < prevTs) return prev;
        return statusPriority(current.status) >= statusPriority(prev.status) ? current : prev;
      };

      if (studentId) {
        const prevById = latestByStudentId.get(studentId);
        latestByStudentId.set(studentId, putLatest(prevById, row));
      }
      if (normalizedName) {
        const prevByName = latestByStudentName.get(normalizedName);
        latestByStudentName.set(normalizedName, putLatest(prevByName, row));
      }
    });

    const merged = students.map((student) => {
      const normalizedStudentName = student.full_name.trim().toLowerCase();
      const latest = latestByStudentId.get(student.id) ?? (normalizedStudentName ? latestByStudentName.get(normalizedStudentName) : undefined);
      if (latest) return latest;
      return {
        id: `missing-${assignmentId}-${student.id}`,
        student_id: student.id,
        student_name: student.full_name,
        work_title: assignment?.title ?? 'Assignment',
        work_id: assignment?.id ?? assignmentId,
        class_id: assignment?.class_id,
        class_name: className,
        work_type: 'assignment' as const,
        submitted_at: null,
        max_points: assignment?.points ?? null,
        queue_badge: 'late' as const,
        status: 'missing' as const,
        score: null,
        feedback: null,
      };
    });

    const rosterIds = new Set(students.map((student) => student.id));
    const rosterNames = new Set(students.map((student) => student.full_name.trim().toLowerCase()).filter(Boolean));
    const extraRows = rows.filter((row) => {
      const studentId = row.student_id?.trim();
      if (studentId && rosterIds.has(studentId)) return false;
      const normalizedName = row.student_name.trim().toLowerCase();
      if (normalizedName && rosterNames.has(normalizedName)) return false;
      return true;
    });
    return [...merged, ...extraRows];
  }, [assignment, assignmentId, className, classStudentsQuery.data, submissionsQuery.data]);

  const stats = useMemo(() => {
    const submitted = submissions.filter((row) => isFinalizedSubmissionStatus(row.status)).length;
    const gradedRows = submissions.filter((row) => row.status === 'graded' || row.status === 'returned');
    const graded = gradedRows.length;
    const avg = gradedRows.reduce((sum, row) => sum + (row.score ?? 0), 0) / Math.max(graded, 1);
    const missing = submissions.filter((row) => row.status === 'missing').length;
    return {
      submissions: submitted,
      graded,
      missing,
      avgScore: graded > 0 ? avg.toFixed(1) : '—',
    };
  }, [submissions]);

  if (assignmentQuery.isLoading || overviewQuery.isLoading) {
    return <Muted>Loading assignment details…</Muted>;
  }

  if (!assignment) {
    return (
      <Page>
        <BackLink to="/classroom-teacher/assignments">
          <FaArrowLeft size={12} aria-hidden />
          Back to assignments
        </BackLink>
        <Muted>Assignment not found.</Muted>
      </Page>
    );
  }

  return (
    <Page>
      <BackLink to={`/classroom-teacher/classes/${encodeURIComponent(assignment.class_id)}/assignments`}>
        <FaArrowLeft size={12} aria-hidden />
        Back to class assignments
      </BackLink>

      <Card>
        <HeroHead>
          <div style={{ minWidth: 0 }}>
            <HeroTitle>{assignment.title}</HeroTitle>
            <HeroMeta>
              {className} · {assignment.points} points ·{' '}
              {assignment.published_at
                ? `Live since ${new Date(assignment.published_at).toLocaleString()}`
                : 'Not live'}{' '}
              · {assignment.due_at ? `Due ${new Date(assignment.due_at).toLocaleString()}` : 'No due date'}
            </HeroMeta>
          </div>
          <HeroActions>
            {!assignment.published_at ? (
              <GoLiveBtn type="button" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
                {publishMutation.isPending ? 'Going live…' : 'Go live'}
              </GoLiveBtn>
            ) : (
              <Status $tone="ok">Live for students</Status>
            )}
            <EditLink to={`/classroom-teacher/assignments/${encodeURIComponent(assignment.id)}/edit`}>
              <FaEdit size={12} aria-hidden />
              Edit assignment
            </EditLink>
          </HeroActions>
        </HeroHead>
      </Card>

      <StatsGrid>
        <StatCard>
          <StatLabel>
            <FaUsers size={12} aria-hidden />
            SUBMISSIONS
          </StatLabel>
          <StatValue>{stats.submissions}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>
            <FaChartBar size={12} aria-hidden />
            GRADED
          </StatLabel>
          <StatValue>{stats.graded}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>
            <FaClock size={12} aria-hidden />
            MISSING
          </StatLabel>
          <StatValue>{stats.missing}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>
            <FaFileAlt size={12} aria-hidden />
            AVG SCORE
          </StatLabel>
          <StatValue>{stats.avgScore}</StatValue>
        </StatCard>
      </StatsGrid>

      <Card>
        <SectionTitle>Instructions & questions</SectionTitle>
        {parsed.instructions.trim() ? (
          <Muted style={{ whiteSpace: 'pre-wrap', color: 'var(--text)' }}>{parsed.instructions}</Muted>
        ) : (
          <Muted>No instructions added.</Muted>
        )}
        {parsed.questions.length > 0 ? (
          <QuestionList style={{ marginTop: '0.8rem' }}>
            {parsed.questions.map((q, idx) => (
              <li key={q.id}>
                {idx + 1}. {questionTypeLabel(q.type)} ({q.points} pt{q.points === 1 ? '' : 's'})
                {q.prompt ? ` — ${q.prompt}` : ''}
              </li>
            ))}
          </QuestionList>
        ) : (
          <Muted style={{ marginTop: '0.65rem' }}>No question blocks added yet.</Muted>
        )}
      </Card>

      <Card>
        <SectionTitle>Submissions</SectionTitle>
        {submissionsQuery.isLoading || classStudentsQuery.isLoading ? (
          <Muted>Loading submissions…</Muted>
        ) : submissions.length === 0 ? (
          <Muted>No submissions yet.</Muted>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Student</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th>Score</Th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((row) => {
                  const badge = statusLabel(row.status);
                  return (
                    <tr key={row.id}>
                      <Td>{row.student_name}</Td>
                      <Td>
                        <Status $tone={badge.tone}>{badge.label}</Status>
                      </Td>
                      <Td>{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'}</Td>
                      <Td>
                        {row.score ?? '—'}
                        {row.max_points ? ` / ${row.max_points}` : ''}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </Page>
  );
};

export default ClassroomTeacherAssignmentDetailPage;
